import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import logoWebp from "../../assets/1.webp";
import logo from "../../assets/1.png";
import Picture from "../../Components/Picture";
import { Link, useLocation, NavLink } from "react-router-dom";
import { NAV_LINKS } from "../../content/navigation";
import { 
  FaTachometerAlt, 
  FaBars, 
  FaTimes, 
  FaUser, 
  FaCog,
  FaBell,
  FaChevronDown
} from "react-icons/fa";
import { useSelector } from "react-redux";
import { selectUser, selectIsAuthenticated, selectIsLoading } from "../../Apis/store/authSlice";
import LogoutButton from "../Logout/LogoutButton";

const Navbar = ({ bg = "bg-brand-900/95" }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();
  
  // Refs for performance optimization
  const scrollTimeoutRef = useRef(null);
  const isScrollingRef = useRef(false);
  
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectUser);
  const isLoading = useSelector(selectIsLoading);

  // Get user role from context - memoized
  const selectedRole = useMemo(() => user?.role || "", [user?.role]);

  // Throttled scroll handler for better performance
  const handleScroll = useCallback(() => {
    if (isScrollingRef.current) return;
    
    isScrollingRef.current = true;
    
    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Throttle scroll events
    scrollTimeoutRef.current = setTimeout(() => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 50);
      
      // كان هنا تتبّع للقسم الظاهر على الشاشة لتمييز المرساة المقابلة
      // في القائمة. القائمة صارت روابط صفحات حقيقية وNavLink يستنتج
      // الحالة النشطة من المسار، فلم يعد له داعٍ — وكان يبحث في DOM عن
      // أربعة عناصر مع كل إطار تمرير، على كل صفحة.
      isScrollingRef.current = false;
    }, 16); // ~60fps throttling
  }, []);

  // Handle scroll effect
  useEffect(() => {
    // كان هنا سطران يضبطان scroll-behavior على عنصر html مباشرة. التمرير
    // السلس معرَّف أصلًا في app.css، ومعه استثناء prefers-reduced-motion —
    // والنمط المباشر يتفوّق على الاستثناء، فكان يفرض التمرير المتحرك على من
    // طلب من نظامه تقليل الحركة. وهم غالبًا من يصيبهم الدوار منها.

    // Use passive listener for better performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Initial call so a page opened mid-scroll starts in the right state
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      // Clear timeout on cleanup
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  // منع تمرير الصفحة خلف قائمة الهاتف وهي مفتوحة — كانت الصفحة تتحرك تحت
  // القائمة فيفقد المستخدم موضعه بمجرد إغلاقها.
  useEffect(() => {
    if (!isMenuOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [isMenuOpen]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      // Clear all timeouts
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      // Reset scrolling flag
      isScrollingRef.current = false;
    };
  }, []);

  // Optimized click outside handlers
  const handleUserMenuClickOutside = useCallback((event) => {
    if (isUserMenuOpen && !event.target.closest('.user-menu')) {
      setIsUserMenuOpen(false);
    }
  }, [isUserMenuOpen]);

  const handleMobileMenuClickOutside = useCallback((event) => {
    if (isMenuOpen && !event.target.closest('.mobile-menu') && !event.target.closest('.mobile-menu-button')) {
      setIsMenuOpen(false);
    }
  }, [isMenuOpen]);

  // Close user menu when clicking outside
  useEffect(() => {
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleUserMenuClickOutside);
      return () => document.removeEventListener('mousedown', handleUserMenuClickOutside);
    }
  }, [isUserMenuOpen, handleUserMenuClickOutside]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleMobileMenuClickOutside);
      return () => document.removeEventListener('mousedown', handleMobileMenuClickOutside);
    }
  }, [isMenuOpen, handleMobileMenuClickOutside]);

  // القائمة تُبنى من content/navigation.js — نفس المصدر الذي يبني
  // مسارات الراوتر، فلا يمكن أن يشير رابط إلى صفحة غير موجودة.
  // كانت هنا قائمة ثابتة من مراسٍ (#academy و#services و#features)
  // انكسرت صامتة عند إعادة بناء الصفحة الرئيسية.
  const navItems = NAV_LINKS;

  // Memoized menu toggle handlers
  const toggleMobileMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  const toggleUserMenu = useCallback(() => {
    setIsUserMenuOpen(prev => !prev);
  }, []);

  /* الشريط شفافًا مسموح فوق واجهة الصفحة الرئيسية الداكنة وحدها. في كل صفحة
     أخرى تبدأ الصفحة بخلفية بيضاء تحت الشريط الثابت، فالشفافية هناك تعني
     نصًا أبيض على أبيض — وهو ما كان يحدث فعلًا.

     والسبب المباشر أن `bg-opacity-20` أداة من Tailwind 3 حُذفت في 4، والمشروع
     على 4.3. الفئة لا تولّد أي CSS (تحقّقنا: صفر تطابق في الملف المبني)، فلم
     يكن للشريط خلفية إطلاقًا لا بشفافية 20% ولا بغيرها. الصيغة المعتمدة في 4
     تدمج الشفافية في اللون نفسه: bg-brand-900/95. */
  const isTransparent = location.pathname === '/' && !isScrolled;

  const navClassName = useMemo(
    () =>
      `fixed top-0 z-50 w-full text-white transition-all duration-300 ${
        isTransparent
          ? 'bg-gradient-to-b from-brand-950/80 to-transparent py-3 md:py-4'
          : `${bg} border-b border-white/10 py-2 shadow-lift backdrop-blur-md md:py-2.5`
      }`,
    [isTransparent, bg]
  );

  return (
    <nav
      className={navClassName}
      dir="rtl"
      aria-label="التنقّل الرئيسي"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Mobile menu button */}
       <button
            className="mobile-menu-button tap-target md:hidden rounded-lg text-white transition-colors hover:bg-white/10"
         onClick={toggleMobileMenu}
            aria-label={isMenuOpen ? "إغلاق القائمة" : "فتح القائمة"}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-nav"
          >
            {isMenuOpen ? (
              <FaTimes className="h-6 w-6" />
            ) : (
              <FaBars className="h-6 w-6" />
            )}
      </button>

          {/* Logo */}
          <Link
            to="/"
            className="shrink-0 flex min-w-11 items-center justify-center gap-2"
            aria-label="أكاديمية الرواد ابن سينا — الصفحة الرئيسية"
          >
            <Picture
                webp={logoWebp}
                fallback={logo}
                alt="Logo"
                width={72}
                height={93}
                loading="eager"
                /* يصغُر مع الشريط عند التمرير، فتبقى مساحة القراءة أوسع */
                className={`w-auto drop-shadow-lg transition-all duration-300 hover:scale-105 ${
                  isTransparent ? "h-14 md:h-20" : "h-11 md:h-14"
                }`}
              />
      </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2 lg:gap-4">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                /* الحالة النشطة كانت تفرَّق باللون وحده، واللون وحده لا يكفي:
                   من لا يميّز الأخضر عن الأبيض لا يعرف أين هو. الشريط السفلي
                   يضيف إشارة غير لونية، وaria-current يبلّغ قارئ الشاشة. */
                className={({ isActive }) =>
                  `relative rounded-lg px-3 py-2 text-sm font-semibold transition-colors after:absolute after:inset-x-3 after:bottom-0.5 after:h-0.5 after:rounded-full after:bg-accent-300 after:transition-transform after:duration-300 ${
                    isActive
                      ? "text-white after:scale-x-100"
                      : "text-brand-100 hover:text-white after:scale-x-0 hover:after:scale-x-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
      </div>

          {/* Right side content */}
          <div className="flex items-center gap-4 ">
            {/* Loading indicator */}
      {isLoading && (
              <div className="flex items-center gap-2 ">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="text-white text-sm drop-shadow-lg">جاري التحميل...</span>
        </div>
      )}

            {/* Authenticated user menu */}
      {isAuthenticated && !isLoading && (
              <div className="relative user-menu">
                <div className="flex items-center gap-3 ">
                  {/* Dashboard link for admin/student */}
                  {(selectedRole === "أدمن" || selectedRole === "طالب") && (
                    <Link 
                      to={selectedRole === "أدمن" ? "/dashboard/GeneralLooka" : "/dashboard/GeneralLooks"}
                      /* كان المرور بالمؤشر يقلب النص إلى الكحلي فوق خلفية شبه
                         شفافة، أي نص داكن على داكن — يختفي عند التحويم. */
                      className="tap-target rounded-lg text-white transition-colors hover:bg-white/10"
                      title="لوحة التحكم"
                      aria-label="لوحة التحكم"
                    >
                      <FaTachometerAlt className="h-5 w-5" />
      </Link>
    )}

                  {/* User profile dropdown */}
                  <div className="relative">
                    <button
                      onClick={toggleUserMenu}
                      className="flex items-center gap-2 rounded-lg p-2 text-white transition-colors hover:bg-white/10"
                      aria-expanded={isUserMenuOpen}
                      aria-haspopup="menu"
                    >
                      <div className="w-8 h-8 shrink-0 bg-brand-700 rounded-full flex items-center justify-center">
                        <FaUser className="h-4 w-4 text-white" />
                      </div>
                      <span className="hidden sm:block text-sm font-medium drop-shadow-lg">
                        {user?.name || "المستخدم"}
                      </span>
                      <FaChevronDown className={`h-3 w-3 transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown menu */}
                    {isUserMenuOpen && (
                      <div className="absolute  left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                        <div className="px-4 py-2 border-b border-gray-200">
                          <p className="text-sm font-medium text-gray-900">{user?.name || "المستخدم"}</p>
                          <p className="text-xs text-gray-500">{selectedRole}</p>
                        </div>
                    
                        <div className="border-t border-gray-200">
                          <LogoutButton className="w-full text-right px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200">
                            تسجيل خروج
    </LogoutButton>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Guest user buttons */}
            {!isAuthenticated && !isLoading && (
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  className="tap-target rounded-lg px-4 text-sm font-semibold text-brand-100 transition-colors hover:bg-white/10 hover:text-white"
                >
                  تسجيل الدخول
                </Link>
                {/* «اشترك الآن» كان كحليًا (‎#0F2A55) فوق شريط كحلي — أي زر
                    الدعوة الأساسي في الموقع كان يذوب في خلفيته. صار بلون
                    الدعوة الأخضر نفسه المستخدم في واجهة الصفحة الرئيسية. */}
                <Link
                  to="/SingUP"
                  className="tap-target hidden rounded-lg bg-accent-500 px-5 text-sm font-bold text-white shadow-card transition-colors hover:bg-accent-600 sm:inline-flex"
                >
                  اشترك الآن
                </Link>
  </div>
)}
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <div
          id="mobile-nav"
          className={`mobile-menu ${isMenuOpen ? 'block' : 'hidden'} md:hidden`}
        >
          {/* خلفية القائمة كانت `bg-[#0F2A55] bg-opacity-95` — والثانية أداة
              محذوفة في Tailwind 4، فكانت الخلفية معتمة تمامًا لا 95%. اللون
              الآن يحمل شفافيته بنفسه. والارتفاع مسقوف مع تمرير داخلي حتى لا
              تتجاوز القائمة الشاشة على الأجهزة القصيرة. */}
          <div className="mt-2 max-h-[calc(100dvh-6rem)] space-y-1 overflow-y-auto rounded-card border border-white/10 bg-brand-800/98 px-2 pt-2 pb-3 shadow-lift backdrop-blur-md">
            {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === "/"}
                  onClick={() => setIsMenuOpen(false)}
                  className={({ isActive }) =>
                    `tap-target block rounded-lg px-4 py-3 text-base font-semibold transition-colors ${
                      isActive
                        ? "border-s-2 border-accent-300 bg-white/10 text-white"
                        : "text-brand-100 hover:bg-white/10 hover:text-white"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}

            {/* Mobile auth buttons */}
        {!isAuthenticated && !isLoading && (
              <div className="mt-2 space-y-2 border-t border-white/20 pt-4">
                <Link
                  to="/login"
                  className="tap-target block rounded-lg px-4 text-base font-semibold text-brand-100 transition-colors hover:bg-white/10 hover:text-white"
                  onClick={() => setIsMenuOpen(false)}
                >
                  تسجيل الدخول
        </Link>
                <Link
                  to="/SingUP"
                  className="tap-target block rounded-lg bg-accent-500 px-4 text-center text-base font-bold text-white transition-colors hover:bg-accent-600"
                  onClick={() => setIsMenuOpen(false)}
                >
                  اشترك الآن
       </Link>
              </div>
      )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
